import React from "react";

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  IbanElement
} from "@stripe/react-stripe-js";

import clsx from "clsx";

import amex from "../../../assets/images/icons/amex.svg";
import visa from "../../../assets/images/icons/visa.svg";
import mastercard from "../../../assets/images/icons/mastercard.svg";
import sepa from "../../../assets/images/icons/sepa.svg";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  makeStyles
} from "@material-ui/core";

function MyInputComponent({
  setCardValues,
  placeholder,
  options,
  name,
  ...props
}) {
  const { component: Component, inputRef, ...other } = props;

  // `Component` will be your `SomeThirdPartyComponent` from below (TextField for our case)
  return (
    <Component
      {...other}
      options={{ ...options, placeholder: "" }}
      onReady={() => {}}
      onChange={event => {
        let error = "";
        if (event.error) {
          error = event.error.message;
        }
        setCardValues({ empty: event.empty, error: error });
      }}
    />
  );
}

const style = {
  base: {
    color: "rgba(0, 0, 0, 0.87)",
    iconColor: "rgba(0, 0, 0, 0.87)",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSmoothing: "antialiased",
    fontSize: "15px",
    fontWeight: 400,
    "::placeholder": {
      color: "rgba(0, 0, 0, 0.5)",
      fontWeight: 400
    }
  },
  invalid: {
    color: "#D93024",
    iconColor: "#D93024"
  }
};

const CARD_ELEMENT_OPTIONS = {
  style: style
};

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ["SEPA"],
  style: style
};

const useStyles = makeStyles(theme => ({
  inputItem: {
    padding: `${theme.spacing(0.5, 0)} !important`
  }
}));

export default function StripePaymentForm({
  cardData,
  classes,
  errors,
  handleChange,
  inputValues,
  userCurrency,
  paymentMethod,
  setPaymentMethod,
  setErrors,
  initialErrorValues
}) {
  const {
    cardNumber,
    cardExpiry,
    cardCvc,
    cardExpiryRef,
    cardNumberRef,
    cardCvcRef,
    setCardNumber,
    setCardExpiry,
    setCardCvc,
    iban,
    setIban,
    ibanRef
  } = cardData;

  const innerClasses = useStyles();

  const handleSelectChange = e => {
    setPaymentMethod(e.target.value);
    setErrors({ ...initialErrorValues });
  };
  const notSepa = paymentMethod === "card" || userCurrency !== "eur";

  return (
    <Box
      width="100%"
      px={3}
      py={4}
      className={classes.borderRightRadius}
      ml={0.25}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className={classes.title}>Payment method</Typography>
        <Box display="flex" className={classes.cardTypes}>
          <Box display={notSepa ? "block" : "none"}>
            <img alt="amex" src={amex} />
            <img alt="mastercard" src={mastercard} />
            <img alt="visa" src={visa} />
          </Box>
          <img
            alt="sepa_debit"
            src={sepa}
            style={{
              display: notSepa ? "none" : "block"
            }}
          />
        </Box>
      </Box>
      {userCurrency === "eur" && (
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="payment method"
            name="payment-method"
            value={paymentMethod}
            onChange={handleSelectChange}
            className={classes.radioGroup}
          >
            <FormControlLabel
              value="card"
              control={<Radio color="primary" />}
              label="Credit card"
            />
            {/* For now we hide the payment option. Do not permanently deleting it in the code.
                After this change the only payment option should be credit card.
                In the future we might only show the SEPA DD payment option after X months of being a paying customer. 
                So Don't remove code commented snippet in bellow !!!
            */}

            {/* <FormControlLabel
              value="sepa_debit"
              control={<Radio color="primary" />}
              label="SEPA Direct Debit"
            /> */}
          </RadioGroup>
        </FormControl>
      )}
      {paymentMethod === "card" || userCurrency !== "eur" ? (
        <>
          <Box className={innerClasses.inputItem}>
            <TextField
              fullWidth
              helperText={errors.cardholderName || " "}
              name="cardholderName"
              inputProps={{
                "data-cy": "cardholderName",
                options: CARD_ELEMENT_OPTIONS,
                maxLength: 50
              }}
              value={inputValues.cardholderName}
              InputLabelProps={{
                required: false,
                classes: {
                  root: classes.label,
                  shrink: classes.labelTextShrinked
                }
              }}
              error={!!errors.cardholderName}
              FormHelperTextProps={{
                classes: { root: classes.helperText }
              }}
              onChange={handleChange}
              label={"Cardholder name"}
              variant="outlined"
            />
          </Box>
          <Box className={clsx("card-wrapper", innerClasses.inputItem)}>
            <>
              <div className={`card-number-wrapper`}>
                <TextField
                  ref={cardNumberRef}
                  InputProps={{
                    inputComponent: MyInputComponent,
                    inputProps: {
                      component: CardNumberElement,
                      setCardValues: setCardNumber,
                      name: "cardNumber",
                      inputRef: cardNumberRef,
                      options: CARD_ELEMENT_OPTIONS
                    }
                  }}
                  InputLabelProps={(() => {
                    // if autofill exists move input label-placeholder
                    return !cardNumber.empty
                      ? {
                          shrink: true
                        }
                      : {};
                  })()}
                  error={
                    !!cardNumber.error ||
                    (cardNumber.empty && !!errors.cardNumber)
                  }
                  helperText={
                    errors.cardNumber && cardNumber.empty
                      ? "Please fill in this field"
                      : cardNumber.error || " "
                  }
                  label={"Card number"}
                  variant="outlined"
                />
              </div>
              <Box display="flex" justifyContent="space-between" mt={1.25}>
                <div className="card-date-wrapper">
                  <TextField
                    ref={cardExpiryRef}
                    InputProps={{
                      inputComponent: MyInputComponent,
                      inputProps: {
                        component: CardExpiryElement,
                        setCardValues: setCardExpiry,
                        name: "cardExpiry",
                        inputRef: cardExpiryRef,
                        options: CARD_ELEMENT_OPTIONS
                      }
                    }}
                    InputLabelProps={(() => {
                      // if autofill exists move input label-placeholder
                      return !cardExpiry.empty
                        ? {
                            shrink: true
                          }
                        : {};
                    })()}
                    error={
                      !!cardExpiry.error ||
                      (cardExpiry.empty && !!errors.cardExpiry)
                    }
                    helperText={
                      errors.cardExpiry && cardExpiry.empty
                        ? "Please fill in this field"
                        : cardExpiry.error || " "
                    }
                    label={"MM/YY"}
                    variant="outlined"
                  />
                </div>
                <div className="card-cvc-wrapper">
                  <TextField
                    ref={cardCvcRef}
                    InputProps={{
                      inputComponent: MyInputComponent,
                      inputProps: {
                        component: CardCvcElement,
                        setCardValues: setCardCvc,
                        name: "cardCvc",
                        inputRef: cardCvcRef
                      }
                    }}
                    InputLabelProps={(() => {
                      // if autofill exists move input label-placeholder
                      return !cardCvc.empty
                        ? {
                            shrink: true
                          }
                        : {};
                    })()}
                    error={
                      !!cardCvc.error || (cardCvc.empty && !!errors.cardCvc)
                    }
                    helperText={
                      errors.cardCvc && cardCvc.empty
                        ? "Please fill in this field"
                        : cardCvc.error || " "
                    }
                    label={"CVC"}
                    variant="outlined"
                    style={{ maxWidth: 64 }}
                  />
                </div>
              </Box>
            </>
          </Box>
        </>
      ) : (
        <Box>
          <Box display="flex">
            <TextField
              fullWidth
              helperText={errors.ibanFullName || " "}
              name="ibanFullName"
              inputProps={{
                "data-cy": "ibanFullName,",
                maxLength: 50
              }}
              value={inputValues.ibanFullName}
              InputLabelProps={{
                required: false,
                classes: {
                  root: classes.label,
                  shrink: classes.labelTextShrinked
                }
              }}
              error={!!errors.ibanFullName}
              FormHelperTextProps={{
                classes: { root: classes.helperText }
              }}
              onChange={handleChange}
              className={clsx(classes.textField, "payment-simple-field")}
              label={"Full name"}
            />
            <TextField
              fullWidth
              helperText={errors.ibanEmail || " "}
              name="ibanEmail"
              inputProps={{
                "data-cy": "ibanEmail,",
                maxLength: 50
              }}
              value={inputValues.ibanEmail}
              InputLabelProps={{
                required: false,
                classes: {
                  root: classes.label,
                  shrink: classes.labelTextShrinked
                }
              }}
              error={!!errors.ibanEmail}
              FormHelperTextProps={{
                classes: { root: classes.helperText }
              }}
              onChange={handleChange}
              className={clsx(classes.textField, "payment-simple-field")}
              label={"Email"}
            />
          </Box>
          <div className="iban-wrapper">
            <TextField
              ref={ibanRef}
              className={classes.textField}
              InputProps={{
                inputComponent: MyInputComponent,
                inputProps: {
                  component: IbanElement,
                  setCardValues: setIban,
                  options: IBAN_ELEMENT_OPTIONS,
                  name: "iban",
                  inputRef: ibanRef
                }
              }}
              InputLabelProps={{
                shrink: true
              }}
              error={!!iban.error || (iban.empty && !!errors.iban)}
              helperText={
                errors.iban && iban.empty
                  ? "Please fill in this field"
                  : iban.error || " "
              }
              label={"IBAN"}
            />
          </div>
        </Box>
      )}
    </Box>
  );
}
